import React, { useEffect, useState } from 'react';
import { Modal, Box, LoadingOverlay } from '@mantine/core';
import { Location } from '../../../@types';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom markers
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const waypointIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

interface TripLocation {
  lat: number;
  lon: number;
}

interface TripRouteMapProps {
  opened: boolean;
  onClose: () => void;
  source: Location;
  destination: Location;
  tripLocations?: TripLocation[];
}

// Component to fit bounds after map is loaded
const FitBounds = ({ points }: { points: [number, number][] }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds.pad(0.1));
    }
  }, [map, points]);

  return null;
};

const TripRouteMap = ({ opened, onClose, source, destination, tripLocations = [] }: TripRouteMapProps) => {
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>([]);
  const [loading, setLoading] = useState(false);

  // All points in sequence
  const allPoints: [number, number][] = [
    [source.lon, source.lat] as [number, number], // OSRM expects [lon, lat]
    ...tripLocations.map(loc => [loc.lon, loc.lat] as [number, number]),
    [destination.lon, destination.lat] as [number, number]
  ];

  // Fetch route from OSRM
  useEffect(() => {
    const fetchRoute = async () => {
      try {
        setLoading(true);
        // Convert points to string format required by OSRM
        const coordinates = allPoints.map(point => point.join(',')).join(';');
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`
        );
        const data = await response.json();
        
        if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates) {
          // OSRM returns coordinates as [lon, lat], convert to [lat, lon] for Leaflet
          const coords = data.routes[0].geometry.coordinates.map(
            (coord: [number, number]) => [coord[1], coord[0]] as [number, number]
          );
          setRouteCoordinates(coords);
        }
      } catch (error) {
        console.error('Error fetching route:', error);
      } finally {
        setLoading(false);
      }
    };

    if (opened && allPoints.length >= 2) {
      fetchRoute();
    }
  }, [opened, allPoints]);

  const center: [number, number] = tripLocations.length > 0 
    ? [tripLocations[0].lat, tripLocations[0].lon] as [number, number]
    : [source.lat, source.lon] as [number, number];

  // Convert points back to [lat, lon] for markers
  const markerPoints: [number, number][] = [
    [source.lat, source.lon] as [number, number],
    ...tripLocations.map(loc => [loc.lat, loc.lon] as [number, number]),
    [destination.lat, destination.lon] as [number, number]
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Trip Route"
      size="xl"
      styles={{
        body: {
          height: '70vh',
          padding: 0,
        },
      }}
    >
      <Box style={{ height: '100%', position: 'relative' }}>
        <LoadingOverlay visible={loading} />
        <MapContainer
          center={center}
          style={{ height: '100%', width: '100%' }}
          zoom={12}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Source point with highlight */}
          <Circle
            center={[source.lat, source.lon]}
            pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.2 }}
            radius={100}
          />
          <Marker position={[source.lat, source.lon]} icon={startIcon}>
            <Popup>Start: {source.name}</Popup>
          </Marker>

          {/* Waypoints with highlights */}
          {tripLocations.map((loc, index) => (
            <React.Fragment key={index}>
              <Circle
                center={[loc.lat, loc.lon]}
                pathOptions={{ color: 'yellow', fillColor: 'yellow', fillOpacity: 0.2 }}
                radius={100}
              />
              <Marker 
                position={[loc.lat, loc.lon]}
                icon={waypointIcon}
              >
                <Popup>Waypoint {index + 1}</Popup>
              </Marker>
            </React.Fragment>
          ))}

          {/* Destination point with highlight */}
          <Circle
            center={[destination.lat, destination.lon]}
            pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.2 }}
            radius={100}
          />
          <Marker position={[destination.lat, destination.lon]} icon={endIcon}>
            <Popup>End: {destination.name}</Popup>
          </Marker>

          {/* Route line */}
          {routeCoordinates.length > 0 && (
            <Polyline
              pathOptions={{ color: '#2196f3', weight: 4, opacity: 0.8 }}
              positions={routeCoordinates}
            />
          )}
          <FitBounds points={markerPoints} />
        </MapContainer>
      </Box>
    </Modal>
  );
};

export default TripRouteMap;
